/**
 * The relationship board's read model (`GET /crm/relationships`).
 *
 * Every figure here is derived by the server, on each read, from what the CRM
 * directory already holds for a contact: dated messages, dated deal rows, first
 * and last seen, and the status of the linked loans. Nothing is stored, and the
 * rules (stage, health, smart lists) live in ONE backend module,
 * `backend/app/services/crm_relationships.py`. The server also returns those
 * rules as sentences under `definitions`, so this file and the screens built on
 * it show the rules verbatim instead of restating thresholds.
 *
 * A contact with no dated record has `stage: null` and `health: null`. The
 * screens say "No score yet" or "—"; they never fill a number in.
 */
import api from './api'

export type RelationshipStage = 'core' | 'active' | 'cooling' | 'new'
export type HealthBand = 'strong' | 'fair' | 'weak'

/** The built-in smart lists, in the order the rail shows them. */
export type SmartListKey = 'desk' | 'core' | 'active' | 'cooling' | 'new' | 'confirm'

export const SMART_LIST_KEYS: readonly SmartListKey[] = ['desk', 'core', 'active', 'cooling', 'new', 'confirm']

export interface RelationshipHealthParts {
  recency: number
  dealFlow: number
  activity: number
}

export interface RelationshipRow {
  id: string
  /** The tenant's own people: never staged or scored, listed only so the
   *  "Needs confirmation" count matches the CRM's own review figure. */
  staff: boolean
  stage: RelationshipStage | null
  stageTitle: string | null
  /** One plain sentence saying why the contact is in this stage. */
  reason: string | null
  /** 0-100, or null: New contacts and contacts with no dated record have none. */
  health: number | null
  healthBand: HealthBand | null
  healthParts: RelationshipHealthParts | null
  lastTouch: string | null
  firstTouch: string | null
  deals: number
  deals12m: number
  deals90d: number
  openDeals: number
  /** Open deals received inside the Active window: the only ones that count
   *  towards a stage. */
  openRecentDeals: number
  funded: number
  /** Deals whose stage is on record; `funded` is a share of these. */
  dealsWithStage: number
  /** Deals whose amount is on record; `volume` covers only these. */
  dealsWithAmount: number
  volume: number | null
  touches: number
  touches90d: number
  /** Distinct dated touches per calendar month, oldest first, current month last. */
  spark: number[]
  needsReview: boolean
  /** Rule-based follow-up, stated as what the record shows. Cooling only. */
  suggestion: string | null
  lists: SmartListKey[]
}

export interface SmartListSummary {
  key: SmartListKey
  label: string
  hint: string
  count: number
}

export interface StageDefinition {
  key: RelationshipStage
  title: string
  hint: string
  rule: string
}

export interface RelationshipDefinitions {
  stages: StageDefinition[]
  health: {
    summary: string
    parts: { key: keyof RelationshipHealthParts; label: string; max: number }[]
    bands: { strong: number; fair: number }
  }
  windows: { newDays: number; activeDays: number; recentDays: number; yearDays: number; sparkMonths: number }
  desk: string
}

export interface RelationshipsResponse {
  asOf: string
  rows: RelationshipRow[]
  lists: SmartListSummary[]
  definitions: RelationshipDefinitions
}

export const crmRelationshipService = {
  get: (): Promise<RelationshipsResponse> => api.get('/crm/relationships').then((r) => r.data as RelationshipsResponse),
}
